import { useEffect, useRef } from 'react';

/**
 * Adds data-revealed="true" to the element when it crosses the threshold.
 * The corresponding CSS in landing.css runs the actual transition.
 */
export function useReveal<T extends HTMLElement = HTMLElement>(threshold = 0.25, once = true) {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      el.setAttribute('data-revealed', 'true');
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.setAttribute('data-revealed', 'true');
            if (once) io.unobserve(entry.target);
          } else if (!once) {
            entry.target.setAttribute('data-revealed', 'false');
          }
        }
      },
      { threshold, rootMargin: '0px 0px -10% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold, once]);
  return ref;
}
